import React, { useMemo, useState } from "react";
import theme from "../../../styles/theme";

/* ============================================================================
   ACTIVITY STRUCTURING BOARD (C1)
   ----------------------------------------------------------------------------
   LOGIQUE
   - C1 reçoit une matière pré-structurée issue de B1/B2
   - une partie reste libre dans un pool
   - l’utilisateur compose des packages pilotables
   - la sélection d’un package alimente C2
============================================================================ */

const T = {
  text: theme?.colors?.text?.primary || "#164D63",
  soft: theme?.colors?.text?.small || "#5C7A89",
  verySoft: theme?.colors?.text?.verysmall || "#7D97A1",

  bg: theme?.colors?.background?.surface || "#ffffff",
  border: theme?.colors?.borders?.default || "#d6dee4",
  borderStrong: theme?.colors?.borders?.panel || "#cfd8dd",

  corelia: theme?.colors?.text?.corelia || "#0e3f56",
  caneva: theme?.colors?.text?.caneva || "#c24c2c",
  green: theme?.colors?.text?.green || "#1A8C74",

  shadowXs: theme?.shadows?.xs || "0 1px 3px rgba(11,39,53,0.08)",
  shadowSm: theme?.shadows?.sm || "0 3px 10px rgba(11,39,53,0.08)",

  radiusLg: theme?.borders?.radius?.lg || "6px",
  radiusXl: theme?.borders?.radius?.xl || "8px",
};

/* ============================================================================
   PRE-STRUCTURATION ISSUE DE B1/B2
   ----------------------------------------------------------------------------
   Les cartes reflètent la matière disponible pour C1 :
   - discipline
   - nature d’effort
   - zone
   - statut d’affectation
============================================================================ */

const sandboxItems = [
  {
    id: "i1",
    label: "Pompes",
    family: "Équipements",
    discipline: "Process",
    zone: "Ligne 1",
    assignedPackageId: "p2",
    intensity: "Critique",
  },
  {
    id: "i2",
    label: "Skids process",
    family: "Équipements",
    discipline: "Process",
    zone: "Ligne 1",
    assignedPackageId: "p2",
    intensity: "Lourd",
  },
  {
    id: "i3",
    label: "Armoires électriques",
    family: "Équipements",
    discipline: "Électricité",
    zone: "Bloc utilités",
    assignedPackageId: "p3",
    intensity: "Critique",
  },
  {
    id: "i4",
    label: "Tuyauterie DN1000",
    family: "Matériaux",
    discipline: "Réseaux",
    zone: "Tronçon primaire",
    assignedPackageId: "p1",
    intensity: "Lourd",
  },
  {
    id: "i5",
    label: "VRD",
    family: "Matériaux",
    discipline: "Réseaux",
    zone: "Plateforme",
    assignedPackageId: "p1",
    intensity: "Standard",
  },
  {
    id: "i6",
    label: "Coffrage",
    family: "Main d’œuvre",
    discipline: "Génie civil",
    zone: "Ouvrage principal",
    assignedPackageId: null,
    intensity: "Standard",
  },
  {
    id: "i7",
    label: "Montage équipements",
    family: "Main d’œuvre",
    discipline: "Process",
    zone: "Ligne 1",
    assignedPackageId: "p2",
    intensity: "Critique",
  },
  {
    id: "i8",
    label: "Câblage",
    family: "Main d’œuvre",
    discipline: "Électricité",
    zone: "Bloc utilités",
    assignedPackageId: "p3",
    intensity: "Critique",
  },
  {
    id: "i9",
    label: "Réservations techniques",
    family: "Main d’œuvre",
    discipline: "Génie civil",
    zone: "Bloc traitement",
    assignedPackageId: null,
    intensity: "Standard",
  },
  {
    id: "i10",
    label: "Supportage tuyauterie",
    family: "Matériaux",
    discipline: "Process",
    zone: "Ligne 1",
    assignedPackageId: null,
    intensity: "Standard",
  },
];

const initialPackages = [
  {
    id: "p1",
    name: "Réseaux primaires DN1000",
    type: "Lot technique",
    status: "À consolider",
    logic: "Par lot métier",
  },
  {
    id: "p2",
    name: "Bloc traitement – ligne 1",
    type: "Sous-système",
    status: "Cohérent",
    logic: "Par système",
  },
  {
    id: "p3",
    name: "Poste électrique principal",
    type: "Système",
    status: "À compléter",
    logic: "Par zone",
  },
];

/* ============================================================================
   HELPERS
============================================================================ */

const getDisciplineColor = (discipline) => {
  switch (discipline) {
    case "Génie civil":
      return "#1E5066";
    case "Réseaux":
      return "#166A58";
    case "Process":
      return "#8A4A13";
    case "Électricité":
      return "#3F5D6E";
    default:
      return T.soft;
  }
};

const getFamilyColor = (family) => {
  switch (family) {
    case "Matériaux":
      return T.corelia;
    case "Main d’œuvre":
      return T.caneva;
    case "Équipements":
      return T.green;
    default:
      return T.soft;
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case "Cohérent":
      return T.green;
    case "À compléter":
      return "#B8621B";
    case "À consolider":
      return T.caneva;
    default:
      return T.soft;
  }
};

const buildPackagePayload = (pkg, items) => {
  const packageItems = items.filter((item) => item.assignedPackageId === pkg.id);
  const disciplineSet = Array.from(new Set(packageItems.map((item) => item.discipline)));
  const zoneSet = Array.from(new Set(packageItems.map((item) => item.zone)));
  const familySet = Array.from(new Set(packageItems.map((item) => item.family)));

  return {
    ...pkg,
    items: packageItems,
    disciplineCount: disciplineSet.length,
    zoneCount: zoneSet.length,
    dominantZone: zoneSet[0] || "-",
    familySet,
  };
};

/* ============================================================================
   UI BLOCKS
============================================================================ */

const HeaderBlock = ({
  packageCount,
  assignedCount,
  freeCount,
  mode,
  setMode,
}) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      gap: 14,
      paddingBottom: 18,
    }}
  >
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <div
        style={{
          fontFamily: theme?.fonts?.body || "inherit",
          fontSize: "0.72rem",
          lineHeight: 1.1,
          color: T.verySoft,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}
      >
        Activity structuring
      </div>

      <div
        style={{
          fontFamily: theme?.fonts?.body || "inherit",
          fontSize: "1.03rem",
          lineHeight: 1.14,
          color: T.text,
          fontWeight: 600,
        }}
      >
        Structuration opérationnelle des packages
      </div>
    </div>

    <div
      style={{
        width: "100%",
        display: "grid",
        gridTemplateColumns: "1fr auto 1fr",
        alignItems: "center",
        gap: 12,
      }}
    >
      {/* GAUCHE — INDICATEURS */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 18,
          flexWrap: "wrap",
          justifyContent: "flex-start",
          minWidth: 0,
        }}
      >
        <MetricInline label="Packages" value={packageCount} />
        <MetricInline label="Éléments affectés" value={assignedCount} />
        <MetricInline
          label="Éléments libres"
          value={freeCount}
          valueColor={freeCount > 0 ? T.caneva : T.green}
        />
      </div>

      {/* CENTRE — FILTRES */}
      <ModeSwitch mode={mode} setMode={setMode} />

      {/* DROITE — LÉGENDE */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 16,
          flexWrap: "wrap",
          minWidth: 0,
        }}
      >
        <LegendDot color={T.green} label="Cohérent" />
        <LegendDot color="#B8621B" label="À compléter" />
        <LegendDot color={T.caneva} label="À consolider" />
      </div>
    </div>
  </div>
);

const MetricInline = ({ label, value, valueColor = T.text }) => (
  <div
    style={{
      display: "flex",
      alignItems: "baseline",
      gap: 6,
    }}
  >
    <span
      style={{
        fontSize: "0.72rem",
        color: T.verySoft,
        lineHeight: 1,
      }}
    >
      {label}
    </span>
    <span
      style={{
        fontSize: "0.82rem",
        color: valueColor,
        lineHeight: 1,
        fontWeight: 600,
      }}
    >
      {value}
    </span>
  </div>
);

const ModeSwitch = ({ mode, setMode }) => {
  const options = ["Par lot métier", "Par système", "Par zone"];

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        flexWrap: "nowrap",
      }}
    >
      {options.map((option) => {
        const active = option === mode;

        return (
          <button
            key={option}
            type="button"
            onClick={() => setMode(option)}
            style={{
              height: 28,
              padding: "0 10px",
              borderRadius: 999,
              border: `1px solid ${active ? "rgba(14,63,86,0.18)" : T.border}`,
              background: active ? "rgba(14,63,86,0.05)" : "#ffffff",
              color: active ? T.corelia : T.soft,
              fontSize: "0.71rem",
              fontWeight: 500,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {option}
          </button>
        );
      })}
    </div>
  );
};

const LegendDot = ({ color, label }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
    <div
      style={{
        width: 10,
        height: 10,
        borderRadius: 999,
        background: color,
      }}
    />
    <span style={{ fontSize: "0.72rem", color: T.verySoft }}>{label}</span>
  </div>
);

const SandboxItemCard = ({ item, compact = false }) => (
  <div
    style={{
      minHeight: compact ? 34 : 44,
      padding: compact ? "7px 8px" : "9px 10px",
      border: `1px solid ${T.border}`,
      borderRadius: T.radiusLg,
      background: "#ffffff",
      display: "flex",
      flexDirection: "column",
      gap: 5,
      boxSizing: "border-box",
      boxShadow: compact ? "none" : T.shadowXs,
    }}
  >
    <div
      style={{
        fontSize: compact ? "0.70rem" : "0.74rem",
        color: T.text,
        lineHeight: 1.2,
      }}
    >
      {item.label}
    </div>

    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexWrap: "wrap",
      }}
    >
      <span
        style={{
          fontSize: "0.64rem",
          color: getDisciplineColor(item.discipline),
          lineHeight: 1,
        }}
      >
        {item.discipline}
      </span>

      <span
        style={{
          fontSize: "0.64rem",
          color: getFamilyColor(item.family),
          lineHeight: 1,
        }}
      >
        {item.family}
      </span>

      <span
        style={{
          fontSize: "0.64rem",
          color: T.verySoft,
          lineHeight: 1,
        }}
      >
        {item.zone}
      </span>
    </div>
  </div>
);

const SandboxRail = ({ items }) => (
  <div
    style={{
      width: 250,
      minWidth: 250,
      border: `1px solid ${T.border}`,
      borderRadius: T.radiusXl,
      background: "linear-gradient(180deg, rgba(252,253,255,1) 0%, rgba(248,250,252,1) 100%)",
      padding: 12,
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      gap: 12,
      flexShrink: 0,
    }}
  >
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        paddingBottom: 2,
      }}
    >
      <div
        style={{
          fontSize: "0.70rem",
          color: T.verySoft,
          textTransform: "uppercase",
          letterSpacing: "0.03em",
          lineHeight: 1,
        }}
      >
        Sandbox
      </div>

      <div
        style={{
          fontSize: "0.80rem",
          color: T.text,
          lineHeight: 1.2,
          fontWeight: 500,
        }}
      >
        Éléments à structurer
      </div>

      <div
        style={{
          fontSize: "0.68rem",
          color: T.soft,
          lineHeight: 1.25,
        }}
      >
        Matière libre issue de B1/B2
      </div>
    </div>

    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      {items.length > 0 ? (
        items.map((item) => <SandboxItemCard key={item.id} item={item} />)
      ) : (
        <div
          style={{
            border: `1px dashed ${T.borderStrong}`,
            borderRadius: T.radiusLg,
            padding: "14px 12px",
            fontSize: "0.72rem",
            color: T.verySoft,
            lineHeight: 1.35,
            textAlign: "center",
            background: "#ffffff",
          }}
        >
          Tous les éléments sont affectés à un package.
        </div>
      )}
    </div>
  </div>
);

const NewPackageColumn = () => (
  <div
    style={{
      minWidth: 250,
      width: 250,
      border: `1px dashed ${T.borderStrong}`,
      borderRadius: T.radiusXl,
      background: "linear-gradient(180deg, rgba(255,255,255,0.94) 0%, rgba(247,249,252,0.96) 100%)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      gap: 10,
      padding: 18,
      boxSizing: "border-box",
      flexShrink: 0,
      cursor: "pointer",
      minHeight: 280,
    }}
  >
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: 999,
        border: `1px solid ${T.border}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: T.corelia,
        fontSize: "1rem",
        lineHeight: 1,
      }}
    >
      +
    </div>

    <div
      style={{
        fontSize: "0.76rem",
        color: T.text,
        textAlign: "center",
        lineHeight: 1.35,
      }}
    >
      Nouveau package
    </div>

    <div
      style={{
        fontSize: "0.68rem",
        color: T.verySoft,
        textAlign: "center",
        lineHeight: 1.35,
        maxWidth: 160,
      }}
    >
      Réceptacle pour créer une nouvelle unité pilotable
    </div>
  </div>
);

const PackageColumn = ({ pkg, isActive, onSelect }) => {
  const subtitle = useMemo(() => {
    const base = [pkg.type, pkg.logic].filter(Boolean).join(" · ");
    const zone = pkg.dominantZone && pkg.dominantZone !== "-" ? ` · ${pkg.dominantZone}` : "";
    return `${base}${zone}`;
  }, [pkg]);

  return (
    <div
      onClick={() => onSelect(pkg)}
      style={{
        minWidth: 300,
        width: 300,
        border: `1px solid ${isActive ? "rgba(14,63,86,0.18)" : T.border}`,
        borderRadius: T.radiusXl,
        background: "#ffffff",
        boxShadow: isActive ? T.shadowSm : "none",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        flexShrink: 0,
        cursor: "pointer",
      }}
    >
      <div
        style={{
          padding: "12px 12px 10px 12px",
          borderBottom: `1px solid ${T.border}`,
          background: "linear-gradient(180deg, rgba(252,253,255,1) 0%, rgba(248,250,252,1) 100%)",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 10,
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
              minWidth: 0,
            }}
          >
            <div
              style={{
                fontSize: "0.70rem",
                color: T.verySoft,
                textTransform: "uppercase",
                letterSpacing: "0.03em",
                lineHeight: 1,
              }}
            >
              Package
            </div>

            <div
              style={{
                fontSize: "0.80rem",
                color: T.text,
                lineHeight: 1.25,
                fontWeight: 500,
              }}
            >
              {pkg.name}
            </div>
          </div>

          <div
            style={{
              flexShrink: 0,
              fontSize: "0.66rem",
              color: getStatusColor(pkg.status),
              lineHeight: 1,
            }}
          >
            {pkg.status}
          </div>
        </div>

        <div
          style={{
            fontSize: "0.68rem",
            color: T.soft,
            lineHeight: 1.25,
          }}
        >
          {subtitle}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            paddingTop: 2,
          }}
        >
          <MiniMetric label="Éléments" value={pkg.items.length} />
          <MiniMetric label="Disciplines" value={pkg.disciplineCount} />
          <MiniMetric label="Zones" value={pkg.zoneCount} />
        </div>
      </div>

      <div
        style={{
          padding: 10,
          display: "flex",
          flexDirection: "column",
          gap: 6,
          minHeight: 240,
        }}
      >
        {pkg.items.length > 0 ? (
          pkg.items.map((item) => <SandboxItemCard key={item.id} item={item} compact />)
        ) : (
          <div
            style={{
              border: `1px dashed ${T.border}`,
              borderRadius: T.radiusLg,
              padding: "14px 10px",
              fontSize: "0.72rem",
              color: T.verySoft,
              lineHeight: 1.35,
              textAlign: "center",
            }}
          >
            Package vide
          </div>
        )}
      </div>
    </div>
  );
};

const MiniMetric = ({ label, value }) => (
  <div
    style={{
      display: "flex",
      alignItems: "baseline",
      gap: 5,
    }}
  >
    <span
      style={{
        fontSize: "0.64rem",
        color: T.verySoft,
        lineHeight: 1,
      }}
    >
      {label}
    </span>
    <span
      style={{
        fontSize: "0.70rem",
        color: T.text,
        lineHeight: 1,
        fontWeight: 600,
      }}
    >
      {value}
    </span>
  </div>
);

/* ============================================================================
   MAIN COMPONENT
============================================================================ */

const ActivityStructuringBoard = ({ onSelectPackage }) => {
  const [mode, setMode] = useState("Par lot métier");
  const [items] = useState(sandboxItems);
  const [packages] = useState(initialPackages);
  const [activeId, setActiveId] = useState(initialPackages[0]?.id || null);

  const freeItems = useMemo(
    () => items.filter((item) => !item.assignedPackageId),
    [items]
  );

  const enrichedPackages = useMemo(
    () => packages.map((pkg) => buildPackagePayload(pkg, items)),
    [packages, items]
  );

  const activePackage = useMemo(
    () => enrichedPackages.find((pkg) => pkg.id === activeId) || enrichedPackages[0] || null,
    [enrichedPackages, activeId]
  );

  const assignedCount = useMemo(
    () => items.filter((item) => item.assignedPackageId).length,
    [items]
  );

  const handleSelect = (pkg) => {
    setActiveId(pkg.id);
    if (onSelectPackage) onSelectPackage(pkg);
  };

  React.useEffect(() => {
    if (activePackage && onSelectPackage) {
      onSelectPackage(activePackage);
    }
  }, [activePackage, onSelectPackage]);

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <HeaderBlock
        packageCount={enrichedPackages.length}
        assignedCount={assignedCount}
        freeCount={freeItems.length}
        mode={mode}
        setMode={setMode}
      />

      <div
        style={{
          width: "100%",
          display: "flex",
          gap: 14,
          overflowX: "auto",
          paddingBottom: 10,
          alignItems: "stretch",
        }}
      >
        <SandboxRail items={freeItems} />
        <NewPackageColumn />

        {enrichedPackages.map((pkg) => (
          <PackageColumn
            key={pkg.id}
            pkg={{
              ...pkg,
              logic: pkg.logic || mode,
            }}
            isActive={pkg.id === activeId}
            onSelect={handleSelect}
          />
        ))}
      </div>
    </div>
  );
};

export default ActivityStructuringBoard;